// The claim form's data model: initial state, validation and conversions to and from the API.
// Pure functions - no React, no I/O - so the rules can be read and tested apart from the page.

/** A fresh, empty form (new arrays each call, so a reset never shares rows with an earlier form). */
export const createInitialFormData = () => ({
    ClaimNumber: '', RO_number: '', VIN: '', ModelName: '', ModelCode: '', PurchasingYear: '', MiledgeIn: '', MiledgeOut: '',
    RO_open_date: '', RepairDate: '', CustomerName: '', RO_Description: '',
    PNMC: '', PNMC_Quantity: '', WarrantyType_Code: '', SymptomCode: '', DamageCode: '',
    RelatedParts: '', RepairLocation: '',
    PartsUsed: [],
    LaborOpDetails: [],
    SubletAmount: '', SubletCode: '', EstimatedAmount: '', ServiceAdvisor: '',
    DealerCode: '', DealerName: '', DealerAddress: ''
});

export const emptyRow = (type) => (type === 'PartsUsed'
    ? { part_name: '', part_quantity: '', part_price: '' }
    : { LaborOpCode: '', LaborHours: '', TechnicianID: '' });

/** The error key of one dynamic row (parts or labour). */
export const rowErrorKey = (type, index) => (type === 'PartsUsed' ? `part_${index}` : `labor_${index}`);

// Empty strings and nulls are empty; 0 is a valid number.
const isEmpty = (val) => {
    if (val === undefined || val === null) return true;
    if (typeof val === 'string') return val.trim() === '';
    return false;
};

/**
 * Field errors for a form ({} when valid). With the vehicle catalogue loaded a model must be
 * selected (ModelCode); without it the free-text ModelName is required instead.
 */
export function validateClaimForm(formData, catalogueReady) {
    const newErrors = {};

    if (isEmpty(formData.VIN)) newErrors.VIN = "Please provide a VIN.";
    if (catalogueReady) {
        if (isEmpty(formData.ModelCode)) newErrors.ModelCode = "Please select the vehicle model.";
    } else if (isEmpty(formData.ModelName)) {
        newErrors.ModelName = "Please provide the model name.";
    }
    if (isEmpty(formData.CustomerName)) newErrors.CustomerName = "Please provide the customer's name.";
    if (isEmpty(formData.EstimatedAmount)) newErrors.EstimatedAmount = "Please provide the estimated total amount.";

    if (!formData.PartsUsed || formData.PartsUsed.length === 0) {
        newErrors.PartsUsed = "At least one part is required.";
    } else {
        formData.PartsUsed.forEach((part, index) => {
            if (isEmpty(part.part_name) || isEmpty(part.part_quantity) || isEmpty(part.part_price)) {
                newErrors[`part_${index}`] = "All part fields are required.";
            }
        });
    }

    if (!formData.LaborOpDetails || formData.LaborOpDetails.length === 0) {
        newErrors.LaborOpDetails = "At least one labor operation is required.";
    } else {
        formData.LaborOpDetails.forEach((op, index) => {
            if (isEmpty(op.LaborOpCode) || isEmpty(op.LaborHours) || isEmpty(op.TechnicianID)) {
                newErrors[`labor_${index}`] = "All labor fields are required.";
            }
        });
    }

    return newErrors;
}

/**
 * Form state from an extraction response. Array rows are mapped defensively in case the AI returns
 * mismatched key casing (e.g. PartName vs part_name); list fields become comma-separated text.
 */
export function formFromExtraction(extractedData) {
    const safePartsUsed = Array.isArray(extractedData.PartsUsed) ? extractedData.PartsUsed.map(p => ({
        part_name: p.part_name !== undefined ? p.part_name : (p.PartName || p.name || ''),
        part_quantity: p.part_quantity !== undefined ? p.part_quantity : (p.Quantity || p.quantity || ''),
        part_price: p.part_price !== undefined ? p.part_price : (p.Price || p.price || '')
    })) : [];

    const safeLaborOpDetails = Array.isArray(extractedData.LaborOpDetails) ? extractedData.LaborOpDetails.map(l => ({
        LaborOpCode: l.LaborOpCode !== undefined ? l.LaborOpCode : (l.Code || l.code || ''),
        LaborHours: l.LaborHours !== undefined ? l.LaborHours : (l.Hours || l.hours || ''),
        TechnicianID: l.TechnicianID !== undefined ? l.TechnicianID : (l.TechID || l.tech_id || '')
    })) : [];

    return {
        ...createInitialFormData(),
        ...extractedData,
        RelatedParts: Array.isArray(extractedData.RelatedParts) ? extractedData.RelatedParts.join(', ') : (extractedData.RelatedParts || ''),
        RepairLocation: Array.isArray(extractedData.RepairLocation) ? extractedData.RepairLocation.join(', ') : (extractedData.RepairLocation || ''),
        PartsUsed: safePartsUsed,
        LaborOpDetails: safeLaborOpDetails,
    };
}

/** The Predict API's JSON shape: numbers parsed, list fields split, an empty ModelCode sent as null. */
export function formatClaimForApi(formData) {
    const data = { ...formData };

    ['MiledgeIn', 'MiledgeOut', 'PNMC_Quantity', 'PurchasingYear', 'EstimatedAmount', 'SubletAmount'].forEach(key => {
        data[key] = data[key] ? parseFloat(data[key]) : null;
    });

    data.ModelCode = data.ModelCode || null;
    data.RelatedParts = data.RelatedParts ? String(data.RelatedParts).split(',').map(s => s.trim()).filter(Boolean) : [];
    data.RepairLocation = data.RepairLocation ? String(data.RepairLocation).split(',').map(s => s.trim()).filter(Boolean) : [];

    data.PartsUsed = data.PartsUsed.map(p => ({
        ...p,
        part_quantity: parseInt(p.part_quantity, 10) || 0,
        part_price: parseFloat(p.part_price) || 0
    }));
    data.LaborOpDetails = data.LaborOpDetails.map(l => ({
        ...l,
        LaborHours: parseFloat(l.LaborHours) || 0
    }));

    return data;
}
